import { SaveV0, type SaveV0Data } from "@dd/schema";
import { corchoLayout } from "../apps/corcho/corchoLayout";
import { useGameStore } from "../state/gameStore";
import { windowStore } from "../wm/useWindowStore";
import type { WindowsLayout } from "../wm/windowStore";
import { db, type SaveRow } from "./db";

/**
 * Saves (F0-T15): autosave con throttle 1 s tras cada evento de dominio;
 * el save es el event log + layout de UI. Export/import como archivo para
 * playtest remoto (C4): el JSON exportado ES un SaveV0 con sessionMeta.
 */

const autoId = (caseId: string) => `auto-${caseId}`;

function snapshot(detectiveName?: string): Omit<SaveRow, "createdAt"> | null {
  const { kase, events } = useGameStore.getState();
  if (!kase) return null;
  return {
    saveId: autoId(kase.id),
    saveVersion: "0.1.0",
    caseId: kase.id,
    ...(detectiveName !== undefined ? { detectiveName } : {}),
    events,
    windowLayout: windowStore.getState().getLayout(),
    corchoPositions: corchoLayout.getState().positions,
    updatedAt: Date.now(),
  };
}

let timer: number | null = null;
let unsubscribe: (() => void) | null = null;

async function persist(detectiveName?: string): Promise<void> {
  const snap = snapshot(detectiveName);
  if (!snap) return;
  const existing = await db.saves.get(snap.saveId);
  await db.saves.put({ ...snap, createdAt: existing?.createdAt ?? Date.now() });
}

/** Arranca el autosave: cada cambio del log (o del corcho) persiste, throttled 1 s. */
export function startAutosave(detectiveName: string): void {
  stopAutosave();
  const schedule = () => {
    if (timer !== null) return;
    timer = window.setTimeout(() => {
      timer = null;
      void persist(detectiveName);
    }, 1000);
  };
  const unsubGame = useGameStore.subscribe((s, prev) => {
    if (s.events !== prev.events) schedule();
  });
  const unsubCorcho = corchoLayout.subscribe(schedule);
  const unsubWm = windowStore.subscribe(schedule);
  unsubscribe = () => {
    unsubGame();
    unsubCorcho();
    unsubWm();
  };
}

export function stopAutosave(): void {
  if (timer !== null) {
    window.clearTimeout(timer);
    timer = null;
  }
  unsubscribe?.();
  unsubscribe = null;
}

export async function loadAutosave(caseId: string): Promise<SaveRow | undefined> {
  return db.saves.get(autoId(caseId));
}

export async function deleteAutosave(caseId: string): Promise<void> {
  await db.saves.delete(autoId(caseId));
}

/** Aplica un save al estado vivo (replay + layout). El caso ya debe estar disponible. */
export function applySave(save: SaveV0Data): void {
  const kase = useGameStore.getState().kase;
  if (!kase || kase.id !== save.caseId) {
    throw new Error(`El save es del caso "${save.caseId}", no del cargado`);
  }
  useGameStore.getState().loadCase(kase, save.events);
  if (save.windowLayout) windowStore.getState().setLayout(save.windowLayout as WindowsLayout);
  corchoLayout.setState({ positions: save.corchoPositions });
}

/** Serializa la partida actual como JSON descargable (playtest remoto, C4/D-14). */
export function exportSaveJson(detectiveName?: string): string | null {
  const snap = snapshot(detectiveName);
  if (!snap) return null;
  const full: SaveV0Data = {
    ...snap,
    createdAt: Date.now(),
    sessionMeta: { exportedAt: Date.now(), userAgent: navigator.userAgent },
  };
  return JSON.stringify(full, null, 2);
}

/** Valida un JSON importado (Zod) y lo aplica. Lanza con mensaje legible si no es un save. */
export function importSaveJson(json: string): void {
  const parsed = SaveV0.safeParse(JSON.parse(json));
  if (!parsed.success) {
    throw new Error(`El archivo no es una partida válida: ${parsed.error.issues[0]?.message}`);
  }
  applySave(parsed.data);
  void persist(parsed.data.detectiveName);
}
